import { ModalPortal } from './ModalPortal';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { compressImage } from '../utils/imageCompressor';
import { mockBackend, Expense, Revenue } from '../services/mockBackend';
import { GoogleGenAI, Type } from "@google/genai";
import { extractDataWithAI } from "../services/aiService";
import Barcode from 'react-barcode';
import { 
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, DollarSign, 
  CheckCircle2, AlertCircle, Clock, Calendar, Check, X, CheckCircle, 
  RotateCcw, Eye, FileText, Paperclip, Truck, ExternalLink, Plus, Layers, Edit, Camera, Trash2, AlertTriangle, FileUp
} from 'lucide-react';

import { AlertModal } from './AlertModal';
import { getLocalDateString } from '../src/lib/utils';

const Agenda: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date()); // Controla o mês visível
  const [selectedDate, setSelectedDate] = useState<string>(getLocalDateString()); // Controla o dia selecionado (YYYY-MM-DD)
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [revenues, setRevenues] = useState<Revenue[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [vehicles, setVehicles] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [alertConfig, setAlertConfig] = useState<{isOpen: boolean, message: string, type: 'error'|'success'|'info'}>({isOpen: false, message: '', type: 'error'});
  const showAlert = (message: string, type: 'error'|'success'|'info' = 'error') => setAlertConfig({isOpen: true, message, type});

  // Estados para Modal de Criação (Novo Lançamento)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRevenueModalOpen, setIsRevenueModalOpen] = useState(false); // Novo
  const [documentMetadata, setDocumentMetadata] = useState<any>(null);
  const [currentExpense, setCurrentExpense] = useState<Partial<Expense>>({});
  const [currentRevenue, setCurrentRevenue] = useState<Partial<Revenue>>({}); // Novo
  const [isEditing, setIsEditing] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [amountInput, setAmountInput] = useState('0,00');
  const [discountInput, setDiscountInput] = useState('0,00');
  const [taxInput, setTaxInput] = useState('0,00');
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [tempAttachment, setTempAttachment] = useState<{name: string, url: string, thumbnailUrl?: string} | null>(null);
  const [installments, setInstallments] = useState<number>(1);

  // Estados para Modal de Pagamento
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [payingExpense, setPayingExpense] = useState<Expense | null>(null);
  const [paidValue, setPaidValue] = useState<number>(0);
  const [paidDate, setPaidDate] = useState<string>(getLocalDateString());

  // Estados para Modal de Reversão de Pagamento
  const [isRevertModalOpen, setIsRevertModalOpen] = useState(false);
  const [revertingExpense, setRevertingExpense] = useState<Expense | null>(null);

  // Estados para Modal de Exclusão
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState<string | null>(null);
  const [itemToDeleteType, setItemToDeleteType] = useState<'expense' | 'revenue' | null>(null);

  // Estados para Modal de Visualização (Detalhes)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewExpense, setViewExpense] = useState<Expense | null>(null);

  // Estado para Preview de Anexo
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handlePreview = (att: { downloadURL: string, thumbnailUrl?: string, name: string }) => {
    const isPdf = att.name.toLowerCase().endsWith('.pdf');
    if (isPdf) {
      setPreviewUrl(att.downloadURL);
      return;
    }
    
    if (att.thumbnailUrl) {
      setPreviewUrl(att.thumbnailUrl.replace(/=s\d+/, '=s2000').replace(/sz=w\d+/, 'sz=w2000'));
      return;
    }

    // Fallback for old Google Drive links
    if (att.downloadURL.includes('drive.google.com/file/d/')) {
      const match = att.downloadURL.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
      if (match && match[1]) {
        setPreviewUrl(`https://drive.google.com/thumbnail?id=${match[1]}&sz=w2000`);
        return;
      }
    } else if (att.downloadURL.includes('drive.google.com/uc?id=')) {
      const match = att.downloadURL.match(/id=([a-zA-Z0-9_-]+)/);
      if (match && match[1]) {
        setPreviewUrl(`https://drive.google.com/thumbnail?id=${match[1]}&sz=w2000`);
        return;
      }
    }
    
    setPreviewUrl(att.downloadURL);
  };

  useEffect(() => {
    if (user) {
      const unsubscribeExp = mockBackend.subscribeToExpenses(user, (expenses) => {
        setExpenses(expenses);
      });
      const unsubscribeRev = mockBackend.subscribeToRevenues(user, (revenues) => {
        setRevenues(revenues);
      });
      loadSettings();
      return () => {
        unsubscribeExp();
        unsubscribeRev();
      };
    }
  }, [user]);

  const loadSettings = async () => {
      const [cats, typs, vecs] = await Promise.all([
        mockBackend.getCategories(),
        mockBackend.getTypes(),
        mockBackend.getVehicles()
      ]);
      setCategories(cats);
      setTypes(typs);
      setVehicles(vecs);
      setIsLoading(false);
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Expenses are handled by subscription
      await loadSettings();
    } finally {
      setIsLoading(false);
    }
  };

  // --- Lógica de Criação (Novo Lançamento) ---
  const openAddModal = () => {
    setTempAttachment(null);
    setInstallments(1);
    // Usa a data selecionada no calendário como padrão
    const initialDate = selectedDate;
    
    setCurrentExpense({
        type: types[0] || 'Boleto',
        status: 'open',
        category: categories[0] || 'Geral',
        dueDate: initialDate,
        createdAt: initialDate, // Sincroniza com a data selecionada na agenda
        attachments: []
    });
    setAmountInput('0,00');
    setIsModalOpen(true);
  };

  const handleEditClick = (expense: Expense) => {
    setTempAttachment(null);
    setInstallments(1);
    setCurrentExpense(expense);
    setAmountInput(expense.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    setIsModalOpen(true);
  };

  const handleAmountInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, ''); 
    const numberValue = parseInt(rawValue || '0', 10) / 100;
    setAmountInput(numberValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    setCurrentExpense(prev => ({ 
        ...prev, 
        amount: numberValue,
        // Sincroniza o valor pago se já estiver marcado como pago
        paidAmount: prev.status === 'paid' ? numberValue : prev.paidAmount
    }));
  };

  const handleRevenueAmountInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    const numberValue = Number(value) / 100;
    setAmountInput(numberValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    setCurrentRevenue(prev => ({ ...prev, valor: numberValue }));
  };

  const handleDiscountInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    const numberValue = Number(value) / 100;
    setDiscountInput(numberValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    setCurrentRevenue(prev => ({ ...prev, desconto: numberValue }));
  };

  const handleSaveRevenue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setIsSaving(true);
    try {
      const finalBruto =
        parseFloat(amountInput.replace(/\./g, "").replace(",", ".")) || 0;
      const finalDesconto =
        parseFloat(discountInput.replace(/\./g, "").replace(",", ".")) || 0;
      const finalTax =
        parseFloat(taxInput.replace(/\./g, "").replace(",", ".")) || 0;
      const finalLiquido = finalBruto - finalDesconto - finalTax;

      const revenueToSave = {
        ...currentRevenue,
        categoria_receita: currentRevenue.categoria_receita || 'Vendas',
        valor_bruto: finalBruto,
        valor_desconto: finalDesconto,
        impostos: finalTax,
        valor_liquido: finalLiquido,
      };

      if (revenueToSave.status === "RECEBIDO") {
        if (!revenueToSave.data_recebimento)
          revenueToSave.data_recebimento =
            revenueToSave.previsao_pagamento ||
            revenueToSave.data_emissao ||
            getLocalDateString();
      }

      if (isEditing && revenueToSave.id) {
        await mockBackend.updateRevenue(user!, revenueToSave.id, revenueToSave);
      } else {
        await mockBackend.addRevenue(
          user!,
          revenueToSave
        );
      }

      setIsRevenueModalOpen(false);
      await loadData();
    } catch (e: any) { showAlert("Erro ao salvar: " + e.message); } 
    finally { setIsSaving(false); }
  };

  const formatCurrency = (value: number | undefined) => {
    return (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newType = e.target.value;
      let newCategory = currentExpense.category;
      if (newType === 'Gasolina' || newType === 'Combustível') {
          newCategory = 'Transporte';
      }
      setCurrentExpense(prev => ({ ...prev, type: newType, category: newCategory }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setIsSaving(true);
    try {
      let mainExpenseId = '';
      const finalAmount = parseFloat(amountInput.replace(/\./g, '').replace(',', '.')) || 0;
      
      const expenseToSave = { 
        ...currentExpense, 
        type: currentExpense.type || types[0] || 'Boleto',
        category: currentExpense.category || categories[0] || 'Geral',
        amount: finalAmount,
        paidAmount: currentExpense.status === 'paid' ? (currentExpense.paidAmount || finalAmount) : null,
        paymentDate: currentExpense.status === 'paid' ? (currentExpense.paymentDate || currentExpense.dueDate) : null
      };

      if (currentExpense.id) {
          await mockBackend.updateExpense(user, currentExpense.id, expenseToSave);
          mainExpenseId = currentExpense.id;
      } else if (expenseToSave.type === 'Boleto' && installments > 1) {
          const baseDueDate = expenseToSave.dueDate ? new Date(expenseToSave.dueDate + 'T12:00:00') : new Date();
          const baseTitle = expenseToSave.title || 'Despesa';
          const originalDay = baseDueDate.getDate();
          for (let i = 0; i < installments; i++) {
              const newDate = new Date(baseDueDate);
              newDate.setMonth(baseDueDate.getMonth() + i);
              if (newDate.getDate() !== originalDay) {
                  newDate.setDate(0); 
              }
              const created = await mockBackend.createExpense(user!, {
                  ...expenseToSave,
                  title: `${baseTitle} (${i + 1}/${installments})`,
                  dueDate: expenseToSave.dueDate ? getLocalDateString(newDate) : undefined
              });
              if (i === 0) mainExpenseId = created.id;
          }
      } else {
          const created = await mockBackend.createExpense(user!, expenseToSave);
          mainExpenseId = created.id;
      }

      if (tempAttachment && mainExpenseId) {
          const newAtt = await mockBackend.addAttachment(user!, mainExpenseId, tempAttachment.url, tempAttachment.name, 'comprovante', user?.id, tempAttachment.thumbnailUrl);
          setExpenses((prev) =>
            prev.map((e) =>
              e.id === mainExpenseId
                ? { ...e, attachments: [...(e.attachments || []), newAtt] }
                : e
            )
          );
      }

      setIsModalOpen(false);
      setDocumentMetadata(null);
      await loadData();
    } catch (e: any) { showAlert("Erro ao salvar: " + e.message); } 
    finally { setIsSaving(false); }
  };

  const handlePdfUploadClick = () => { pdfInputRef.current?.click(); };

  const handlePdfChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsUploadingPdf(true);
    try {
      const base64EncodeString = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
              const result = reader.result as string;
              resolve(result.split(',')[1]);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
      });

      const mimeType = file.type === "application/pdf" ? "application/pdf" : "image/jpeg";
      const prompt = "Classifique este documento como 'despesa' (boleto, comprovante de pagamento, comprovante de PIX) ou 'receita' (nota fiscal de venda/serviço). Extraia os dados relevantes. Se for despesa, extraia supplierName, amount, dueDate, codigo_barras, linha_digitavel, pix, description, title. Se for receita, extraia cliente, descricao, numero_nota, serie_nota, tipo_nota, data_emissao, previsao_pagamento, valor_liquido, valor_bruto, impostos, valor_desconto.";
      const schema = {
        type: Type.OBJECT,
        properties: {
          type: { type: Type.STRING, description: "Classificação principal: 'despesa' ou 'receita'" },
          docType: { type: Type.STRING, description: "Tipo específico: 'boleto', 'comprovante_pix', 'nfe', etc." },
          data: { 
            type: Type.OBJECT,
            properties: {
              // Campos de despesa
              supplierName: { type: Type.STRING },
              amount: { type: Type.NUMBER },
              dueDate: { type: Type.STRING },
              codigo_barras: { type: Type.STRING },
              linha_digitavel: { type: Type.STRING },
              pix: { type: Type.STRING },
              description: { type: Type.STRING },
              title: { type: Type.STRING },
              // Campos de receita
              cliente: { type: Type.STRING },
              numero_nota: { type: Type.STRING },
              serie_nota: { type: Type.STRING },
              tipo_nota: { type: Type.STRING },
              data_emissao: { type: Type.STRING },
              previsao_pagamento: { type: Type.STRING },
              valor_liquido: { type: Type.NUMBER },
              valor_bruto: { type: Type.NUMBER },
              impostos: { type: Type.NUMBER },
              valor_desconto: { type: Type.NUMBER }
            }
          }
        }
      };

      // Inicia o upload em paralelo com a extração da IA
      const uploadPromise = (async () => {
        try {
          let uploadBase64 = base64EncodeString;
          if (file.type.startsWith('image/')) {
            uploadBase64 = await compressImage(file);
          }
          return await mockBackend.uploadImage(uploadBase64, file.name, file.type);
        } catch (e) {
          console.error("Failed to upload AI attachment", e);
          return null;
        }
      })();

      const extractedData = await extractDataWithAI(base64EncodeString, mimeType, prompt, schema);
      const uploadResult = await uploadPromise;
      
      const docType = extractedData.docType;
      const isDespesa = extractedData.type === 'despesa' || docType === 'boleto' || docType === 'comprovante_pix';
      const dataObj = extractedData.data || {};
      
      if (uploadResult) {
        setTempAttachment({ name: file.name, url: uploadResult.url, thumbnailUrl: uploadResult.thumbnailUrl });
      } else {
        const localUrl = URL.createObjectURL(file);
        setTempAttachment({ name: file.name, url: localUrl });
      }

      if (isDespesa) {
          const expenseData: any = {};
          if (dataObj.amount !== undefined) expenseData.amount = dataObj.amount;
          if (dataObj.dueDate) expenseData.dueDate = dataObj.dueDate;
          if (dataObj.supplierName) expenseData.supplierName = dataObj.supplierName;
          if (dataObj.codigo_barras) expenseData.codigo_barras = dataObj.codigo_barras;
          if (dataObj.linha_digitavel) expenseData.linha_digitavel = dataObj.linha_digitavel;
          if (dataObj.pix) expenseData.pix = dataObj.pix;
          if (dataObj.description) expenseData.description = dataObj.description;
          if (dataObj.title) expenseData.title = dataObj.title;

          const extractedType = docType === 'boleto' ? 'Boleto' : (docType === 'comprovante_pix' ? 'PIX' : 'Geral');
          const extractedCategory = 'Compras';

          setCurrentExpense(prev => ({
              ...prev,
              ...expenseData,
              type: types.includes(extractedType) ? extractedType : (types[0] || 'Boleto'),
              category: categories.includes(extractedCategory) ? extractedCategory : (categories[0] || 'Geral'),
              status: 'open',
              createdAt: selectedDate
          }));
          if (expenseData.amount !== undefined) {
              setAmountInput(expenseData.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
          }
          setIsModalOpen(true);
      } else {
          const revenueData: any = {};
          if (dataObj.valor_bruto !== undefined) revenueData.valor_bruto = dataObj.valor_bruto;
          if (dataObj.data_emissao) revenueData.data_emissao = dataObj.data_emissao;
          if (dataObj.previsao_pagamento) revenueData.previsao_pagamento = dataObj.previsao_pagamento;
          if (dataObj.cliente) revenueData.cliente = dataObj.cliente;
          if (dataObj.numero_nota) revenueData.numero_nota = dataObj.numero_nota;
          if (dataObj.serie_nota) revenueData.serie_nota = dataObj.serie_nota;
          if (dataObj.descricao) revenueData.descricao = dataObj.descricao;
          if (dataObj.valor_desconto !== undefined) revenueData.valor_desconto = dataObj.valor_desconto;
          if (dataObj.impostos !== undefined) revenueData.impostos = dataObj.impostos;
          if (dataObj.valor_liquido !== undefined) revenueData.valor_liquido = dataObj.valor_liquido;

          setCurrentRevenue(prev => ({
              categoria_receita: 'Vendas',
              status: 'open',
              ...prev,
              ...revenueData,
              data_emissao: revenueData.data_emissao || selectedDate
          }));
          setIsRevenueModalOpen(true);
      }

      if (extractedData.docType) {
        setDocumentMetadata({ tipo_documento: extractedData.docType });
      }

      showAlert('Dados extraídos com sucesso via IA. Por favor, revise os campos antes de salvar.', 'success');
    } catch (err: any) { 
      showAlert("Erro ao ler documento com IA: " + err.message); 
    } finally { 
      setIsUploadingPdf(false); 
      if (pdfInputRef.current) pdfInputRef.current.value = ''; 
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Preview instantâneo
    const localUrl = URL.createObjectURL(file);
    setTempAttachment({ name: file.name, url: localUrl });

    setIsUploading(true);
    try {
      const compressedBase64 = await compressImage(file);
      const uploadResult = await mockBackend.uploadImage(compressedBase64, file.name, file.type);
      setTempAttachment({ name: file.name, url: uploadResult.url, thumbnailUrl: uploadResult.thumbnailUrl });
    } catch (err: any) { 
      showAlert(err.message); 
      setTempAttachment(null);
    } finally { 
      setIsUploading(false); 
      if (fileInputRef.current) fileInputRef.current.value = ''; 
    }
  };

  const handleDeleteAttachment = async (attId: string) => {
    if (!user || isViewOnly) return;
    if (!confirm("Excluir anexo?")) return;
    try {
      await mockBackend.deleteAttachment(user, attId);
      setCurrentExpense((prev) => ({
        ...prev,
        attachments: prev.attachments?.filter((a) => a.id !== attId),
      }));
    } catch (err: any) {
      showAlert(err.message);
    }
  };

  // --- Lógica de Exclusão ---
  const handleDeleteClick = (id: string, type: 'expense' | 'revenue') => {
    if (!user) return;
    if (user.role !== 'admin') {
      showAlert("Apenas administradores podem excluir.");
      return;
    }
    setExpenseToDelete(id);
    setItemToDeleteType(type);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!expenseToDelete || !user || !itemToDeleteType) return;
    try {
      if (itemToDeleteType === 'expense') {
        await mockBackend.deleteExpense(user, expenseToDelete);
      } else {
        await mockBackend.deleteRevenue(user, expenseToDelete);
      }
      await loadData();
      setIsDeleteModalOpen(false);
      setExpenseToDelete(null);
      setItemToDeleteType(null);
    } catch (e: any) { showAlert(e.message); }
  };

  // --- Lógica de Alteração de Status ---
  const handleMarkAsPaid = async (expense: Expense) => {
    if (!user) return;
    const canEdit = user.role === 'admin' || expense.createdBy === user.id;
    if (!canEdit) return;

    if (expense.status === 'paid') {
        setRevertingExpense(expense);
        setIsRevertModalOpen(true);
    } else {
        setPayingExpense(expense);
        setPaidValue(expense.amount);
        setPaidDate(getLocalDateString());
        setIsPaymentModalOpen(true);
    }
  };

  const handleConfirmPayment = async () => {
      if (!payingExpense || !user) return;
      try {
          await mockBackend.updateExpense(user, payingExpense.id, { 
              status: 'paid', 
              paymentDate: paidDate, 
              paidAmount: paidValue 
          });
          setIsPaymentModalOpen(false);
          await loadData();
      } catch (err: any) { showAlert(err.message); }
  };

  const handleConfirmRevert = async (newStatus: 'open' | 'overdue') => {
      if (!revertingExpense || !user) return;
      try {
          await mockBackend.updateExpense(user, revertingExpense.id, { 
              status: newStatus, 
              paymentDate: null, 
              paidAmount: null 
          });
          setIsRevertModalOpen(false);
          await loadData();
      } catch (err: any) { showAlert(err.message); }
  };

  // --- Lógica de Visualização ---
  const handleViewDetails = (expense: Expense) => {
      setViewExpense(expense);
      setIsViewModalOpen(true);
  };

  // --- Lógica do Calendário ---
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay(); // 0 = Domingo
  
  // Formatando Mês conforme solicitado
  const monthString = currentDate.toLocaleDateString('pt-BR', { month: 'long' });
  const monthNumber = String(month + 1).padStart(2, '0');

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const itemsByDay = useMemo(() => {
    const map: Record<string, { type: 'expense' | 'revenue', data: any }[]> = {};
    
    expenses.forEach(exp => {
      // Prioridade: Vencimento (dueDate), se não houver, usa createdAt
      let targetDate = exp.dueDate || exp.createdAt;
      
      if (!targetDate) return;
      const dateKey = targetDate.split('T')[0];
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push({ type: 'expense', data: exp });
    });

    revenues.forEach(rev => {
      let targetDate = rev.created_at;
      if (rev.status === 'RECEBIDO' && rev.data_recebimento) targetDate = rev.data_recebimento;
      else if (rev.previsao_pagamento) targetDate = rev.previsao_pagamento;
      else if (rev.data_emissao) targetDate = rev.data_emissao;

      if (!targetDate) return;
      const dateKey = targetDate.split('T')[0];
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push({ type: 'revenue', data: rev });
    });

    return map;
  }, [expenses, revenues]);

  // Lista APENAS de vencimentos (Abertos/Vencidos) do mês atual
  const monthItems = useMemo(() => {
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    const exps = expenses.filter(exp => {
      if (exp.status === 'paid') return false;
      const dateStr = exp.dueDate || exp.createdAt;
      if (!dateStr) return false;
      const [y, m] = dateStr.split('-').map(Number);
      return y === currentYear && (m - 1) === currentMonth;
    }).map(e => ({ type: 'expense' as const, data: e, date: e.dueDate || e.createdAt }));

    const revs = revenues.filter(rev => {
      if (rev.status === 'RECEBIDO' || rev.status === 'CANCELADO') return false;
      const dateStr = rev.previsao_pagamento || rev.data_emissao || rev.created_at;
      if (!dateStr) return false;
      const [y, m] = dateStr.split('-').map(Number);
      return y === currentYear && (m - 1) === currentMonth;
    }).map(r => ({ type: 'revenue' as const, data: r, date: r.previsao_pagamento || r.data_emissao || r.created_at }));

    return [...exps, ...revs].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  }, [expenses, revenues, currentDate]);

  const selectedItems = useMemo(() => {
    return itemsByDay[selectedDate] || [];
  }, [selectedDate, itemsByDay]);

  const dailyTotalExpenses = selectedItems.filter(i => i.type === 'expense').reduce((acc, curr) => acc + (curr.data.paidAmount || curr.data.amount), 0);
  const dailyTotalRevenues = selectedItems.filter(i => i.type === 'revenue').reduce((acc, curr) => acc + curr.data.valor_liquido, 0);
  const dailyBalance = dailyTotalRevenues - dailyTotalExpenses;

  const getStatusBadge = (status: string, type: 'expense' | 'revenue') => {
    if (type === 'revenue') {
        if (status === 'RECEBIDO') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-green-100 text-green-700 border border-green-200 shadow-sm"><Check size={10}/> Recebido</span>;
        if (status === 'VENCIDO') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-red-100 text-red-700 border border-red-200 shadow-sm"><AlertCircle size={10}/> Vencido</span>;
        if (status === 'CANCELADO') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-slate-100 text-slate-700 border border-slate-200 shadow-sm"><X size={10}/> Cancelado</span>;
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-blue-100 text-blue-700 border border-blue-200 shadow-sm"><Calendar size={10}/> Aberto</span>;
    }
    if (status === 'paid') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-green-100 text-green-700 border border-green-200 shadow-sm"><Check size={10}/> Pago</span>;
    if (status === 'overdue') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-red-100 text-red-700 border border-red-200 shadow-sm"><AlertCircle size={10}/> Vencido</span>;
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-blue-100 text-blue-700 border border-blue-200 shadow-sm"><Calendar size={10}/> Aberto</span>;
  };

  const renderCalendarDays = () => {
    const days = [];
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="h-10 w-full"></div>);
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const isSelected = selectedDate === dateString;
      const isToday = dateString === getLocalDateString();
      const dayItems = itemsByDay[dateString] || [];
      const hasEvents = dayItems.length > 0;
      const hasPending = dayItems.some(i => i.type === 'expense' && (i.data.status === 'overdue' || i.data.status === 'open') || i.type === 'revenue' && (i.data.status === 'EM_ABERTO' || i.data.status === 'VENCIDO'));
      const allPaid = hasEvents && dayItems.every(i => i.type === 'expense' && i.data.status === 'paid' || i.type === 'revenue' && i.data.status === 'RECEBIDO');
      days.push(
        <button
          key={d}
          onClick={() => setSelectedDate(dateString)}
          className={`h-12 w-full flex flex-col items-center justify-center rounded-lg transition-all relative border border-slate-100 dark:border-slate-700
            ${isSelected ? 'bg-brand-600 text-white shadow-md' : 'hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-800'}
            ${!isSelected && hasPending ? 'text-red-600 dark:text-red-400 font-extrabold' : (!isSelected ? 'text-slate-700 dark:text-slate-200 font-medium' : '')}
            ${isToday && !isSelected ? 'border border-brand-300 bg-brand-50' : ''}
          `}
        >
          <span className="text-sm">{d}</span>
          <div className="flex gap-0.5 mt-1">
            {hasEvents && (
               <span className={`w-1.5 h-1.5 rounded-full ${
                  isSelected ? 'bg-white dark:bg-slate-900' : 
                  hasPending ? 'bg-red-600' : 
                  allPaid ? 'bg-green-500' : 'bg-blue-400'
               }`}></span>
            )}
          </div>
        </button>
      );
    }
    return days;
  };

  const showVehicleSelect = currentExpense.type && (
    (currentExpense.type || "").toLowerCase().includes('gasolina') || 
    (currentExpense.type || "").toLowerCase().includes('combustível')
  );

  return (
    <div className="font-sans animate-in fade-in duration-500 lg:h-[calc(100vh-120px)] flex flex-col">
      <AlertModal 
        isOpen={alertConfig.isOpen} 
        message={alertConfig.message} 
        type={alertConfig.type} 
        onClose={() => setAlertConfig(prev => ({...prev, isOpen: false}))} 
      />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-brand-100 text-brand-700 rounded-lg">
            <CalendarIcon size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200">Agenda Financeira</h2>
            <p className="text-slate-500 text-xs">Controle seus vencimentos dia a dia.</p>
          </div>
        </div>
        <div className="flex gap-2 self-start sm:self-auto">

           <button 
              onClick={openAddModal} 
              className="bg-green-600 text-white px-4 py-2 rounded font-bold uppercase text-xs flex items-center gap-2 hover:bg-green-700 transition-colors shadow-sm"
           >
              <Plus size={16} /> Novo Lançamento
           </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row gap-6 lg:overflow-hidden">
        {/* LADO ESQUERDO: Calendário e Lista do Mês */}
        <div className="w-full lg:w-5/12 xl:w-4/12 flex flex-col bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm overflow-hidden lg:h-full">
           <div className="shrink-0 p-4 bg-slate-50 dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <button onClick={prevMonth} className="p-2 hover:bg-white dark:hover:bg-slate-800 rounded-full text-slate-600 dark:text-slate-400 hover:shadow-sm transition-all"><ChevronLeft size={20}/></button>
              
              <h3 className="font-bold text-slate-700 dark:text-slate-200 text-lg flex items-center gap-2">
                 <span className="flex items-center justify-center w-8 h-8 bg-brand-600 text-white rounded-full text-sm shadow-sm">
                    {monthNumber}
                 </span>
                 <span className="capitalize">{monthString} {year}</span>
              </h3>

              <button onClick={nextMonth} className="p-2 hover:bg-white dark:hover:bg-slate-800 rounded-full text-slate-600 dark:text-slate-400 hover:shadow-sm transition-all"><ChevronRight size={20}/></button>
           </div>
           <div className="shrink-0 grid grid-cols-7 text-center py-2 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800">
              {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((day, i) => (
                 <span key={i} className="text-xs font-bold text-slate-400 uppercase">{day}</span>
              ))}
           </div>
           <div className="shrink-0 grid grid-cols-7 gap-1 p-2">
              {renderCalendarDays()}
           </div>
           <div className="shrink-0 p-3 bg-slate-50 dark:bg-slate-900 border-t border-b border-slate-100 dark:border-slate-800 flex justify-between gap-4 text-[10px] text-slate-500 dark:text-slate-400">
              <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-600"></span> Pendente</div>
              <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500"></span> Pago</div>
           </div>
           
           {/* LISTA DE VENCIMENTOS DO MÊS */}
           <div className="flex-1 overflow-y-auto p-0 bg-slate-50/30 dark:bg-slate-900/50 max-h-[80vh] lg:max-h-none">
               <div className="p-3 bg-slate-100/50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 sticky top-0 backdrop-blur-sm z-10 flex justify-between items-center">
                   <h4 className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase">Vencimentos Pendentes ({monthItems.length})</h4>
               </div>
               <div className="p-2 space-y-2">
                   {monthItems.map(item => {
                       const isExp = item.type === 'expense';
                       const data = item.data;
                       const status = data.status;
                       
                       let cardStyle = "bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 text-blue-900 dark:text-blue-200";
                       if (isExp) {
                           if (status === 'paid') cardStyle = "bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 text-green-900 dark:text-green-200";
                           if (status === 'overdue') cardStyle = "bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 text-red-900 dark:text-red-200";
                       } else {
                           cardStyle = "bg-purple-50 dark:bg-purple-900/20 border-l-4 border-purple-500 text-purple-900 dark:text-purple-200";
                           if (status === 'RECEBIDO') cardStyle = "bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 text-green-900 dark:text-green-200";
                           if (status === 'VENCIDO') cardStyle = "bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 text-red-900 dark:text-red-200";
                       }

                       return (
                           <div 
                               key={`${item.type}-${data.id}`} 
                               onClick={() => setSelectedDate(item.date.split('T')[0])} 
                               className={`p-3 rounded shadow-sm cursor-pointer transition-all hover:brightness-95 flex items-center justify-between group ${cardStyle}`}
                           >
                               <div className="flex items-center gap-3">
                                   <div>
                                       <p className="text-xs font-bold truncate max-w-[150px]">{isExp ? data.title : data.descricao}</p>
                                       <p className="text-[10px] opacity-80">
                                           {new Date(item.date.includes('T') ? item.date : `${item.date}T12:00:00`).toLocaleDateString('pt-BR')}
                                       </p>
                                   </div>
                               </div>
                               <div className="text-right">
                                   <p className="text-xs font-bold">{formatCurrency(isExp ? data.amount : data.valor_liquido)}</p>
                                   <p className="text-[10px] uppercase opacity-80">{isExp ? (status === 'overdue' ? 'Vencido' : status === 'paid' ? 'Pago' : 'Aberto') : (status === 'VENCIDO' ? 'Vencido' : status === 'RECEBIDO' ? 'Recebido' : 'Aberto')}</p>
                               </div>
                           </div>
                       );
                   })}
                   {monthItems.length === 0 && (
                       <div className="p-4 text-center text-xs text-slate-400 italic">Nenhum vencimento pendente neste mês.</div>
                   )}
               </div>
           </div>
        </div>

        {/* LADO DIREITO: Detalhes do Dia */}
        <div className="flex-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm flex flex-col overflow-hidden lg:h-full min-h-[400px]">
           <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 flex justify-between items-center shrink-0">
              <div>
                 <h3 className="font-bold text-slate-700 dark:text-slate-200 text-lg">
                    {new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', weekday: 'long' })}
                 </h3>
                 <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {selectedItems.length} lançamentos encontrados
                 </p>
              </div>
              <div className="text-right">
                 <p className="text-[10px] font-bold text-slate-400 uppercase">Saldo do Dia</p>
                 <p className={`text-xl font-extrabold ${dailyBalance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{formatCurrency(dailyBalance)}</p>
              </div>
           </div>
           <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/30 dark:bg-slate-900/50">
              {selectedItems.length === 0 ? (
                 <div className="h-full flex flex-col items-center justify-center text-slate-400 opacity-60">
                    <CalendarIcon size={48} className="mb-3 stroke-1"/>
                    <p className="text-sm">Nenhum lançamento para esta data.</p>
                 </div>
              ) : (
                 selectedItems.map(item => {
                    const isExp = item.type === 'expense';
                    const data = item.data;
                    const canEditItem = user?.role === 'admin' || data.createdBy === user?.id;
                    return (
                    <div key={`${item.type}-${data.id}`} className={`bg-white dark:bg-slate-800 border-l-4 ${isExp ? 'border-blue-500' : 'border-purple-500'} border-y border-r border-y-slate-200 border-r-slate-200 dark:border-y-slate-700 dark:border-r-slate-700 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow flex flex-col sm:flex-row sm:items-center justify-between gap-4 group`}>
                       <div className="flex flex-col gap-0.5 cursor-pointer flex-1" onClick={() => isExp ? handleViewDetails(data) : navigate('/revenues', { state: { viewRevenueId: data.id } })}>
                          <h4 className="font-bold text-slate-800 dark:text-slate-200 text-sm group-hover:text-brand-700 transition-colors">
                             {isExp ? data.title : data.descricao}
                             {!isExp && <span className="ml-2 text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded border border-purple-200">Nota Fiscal</span>}
                          </h4>
                          <div className="flex flex-wrap items-center gap-2 text-[10px] text-slate-500 dark:text-slate-400">
                             <span className="bg-slate-100 dark:bg-slate-700 px-1 py-0.5 rounded border border-slate-200 dark:border-slate-600">{isExp ? (data.type || 'Sem Tipo') : (data.tipo_nota || 'Sem Tipo')}</span>
                             <span className="bg-slate-100 dark:bg-slate-700 px-1 py-0.5 rounded border border-slate-200 dark:border-slate-600">{isExp ? (data.category || 'Sem Categoria') : (data.categoria_receita || 'Sem Categoria')}</span>
                             <span className="truncate max-w-[150px] sm:max-w-none">• {isExp ? data.supplierName : data.cliente}</span>
                          </div>
                       </div>
                       <div className="flex items-center gap-3 self-end sm:self-auto">
                          <div className="text-right">
                             <div className={`font-bold text-sm ${isExp ? 'text-slate-800 dark:text-slate-200' : 'text-green-600 dark:text-green-400'}`}>
                                {isExp ? (data.status === 'paid' && data.paidAmount !== undefined && data.paidAmount !== null 
                                    ? formatCurrency(data.paidAmount) 
                                    : formatCurrency(data.amount)) : formatCurrency(data.valor_liquido)
                                }
                             </div>
                             <div className="text-[10px] font-medium uppercase text-slate-400 dark:text-slate-500">
                                {isExp ? data.type : data.tipo_nota} {isExp && data.status === 'paid' && '(Pago)'} {!isExp && data.status === 'RECEBIDO' && '(Recebido)'}
                                {!isExp && data.previsao_pagamento && ` - Prev. ${data.previsao_pagamento.split('T')[0].split('-').reverse().join('/')}`}
                             </div>
                          </div>
                          <div className="text-slate-300 font-light text-lg">|</div>
                          <div 
                             onClick={() => isExp ? handleMarkAsPaid(data) : null}
                             className={canEditItem && isExp ? 'cursor-pointer hover:opacity-80 transition-opacity active:scale-95' : ''}
                             title={canEditItem && isExp ? "Alterar Status" : ""}
                          >
                             {getStatusBadge(data.status, item.type)}
                          </div>
                          {canEditItem && (
                             <div 
                                onClick={(e) => { e.stopPropagation(); isExp ? handleEditClick(data) : navigate('/revenues', { state: { editRevenueId: data.id } }); }}
                                className="cursor-pointer text-slate-400 hover:text-blue-600 transition-colors p-1"
                                title="Editar"
                             >
                                <Edit size={16} />
                             </div>
                          )}
                          {user?.role === 'admin' && (
                             <div 
                                onClick={(e) => { e.stopPropagation(); handleDeleteClick(data.id, item.type); }}
                                className="cursor-pointer text-slate-400 hover:text-red-600 transition-colors p-1"
                                title="Excluir"
                             >
                                <Trash2 size={16} />
                             </div>
                          )}
                       </div>
                    </div>
                 )})
              )}
           </div>
        </div>
      </div>

      {/* Modal Criar Lançamento */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 z-50 flex items-center justify-center p-3 sm:p-4 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl w-full max-w-5xl rounded-2xl flex flex-col max-h-[92vh] sm:max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-[#4c1d95] via-[#5b21b6] to-[#3b0764] text-white px-6 py-3.5 flex justify-between items-center shrink-0 shadow-md">
              <h3 className="font-bold text-base flex items-center gap-2 tracking-wide">
                {currentExpense.id ? <Edit size={18} /> : <Plus size={18} />} 
                {currentExpense.id ? 'Editar Lançamento' : 'Novo Lançamento na Agenda'}
              </h3>
              <button 
                onClick={() => {
                  setIsModalOpen(false);
                  setDocumentMetadata(null);
                }} 
                className="text-white/80 hover:text-white hover:bg-white/10 p-1.5 rounded-full transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">
              {documentMetadata && (
                <div className={`p-4 rounded-xl border ${documentMetadata.precisa_revisao ? 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800/50 dark:text-amber-200' : 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800/50 dark:text-green-200'}`}>
                  <div className="flex items-center gap-2 font-semibold mb-2">
                    {documentMetadata.precisa_revisao ? <AlertCircle size={20} /> : <CheckCircle size={20} />}
                    <span>
                      {documentMetadata.precisa_revisao ? 'Revisão Necessária' : 'Extração Confiável'}
                    </span>
                  </div>
                  <div className="text-sm space-y-1">
                    <p><strong>Tipo de Documento:</strong> {documentMetadata.tipo_documento}</p>
                    <p><strong>Confiança Média:</strong> {(documentMetadata.score_confianca * 100).toFixed(0)}%</p>
                    {documentMetadata.erros_validacao?.length > 0 && (
                      <div className="mt-2 text-red-600 dark:text-red-400">
                        <strong>Avisos de Validação:</strong>
                        <ul className="list-disc pl-5 mt-1">
                          {documentMetadata.erros_validacao.map((erro: string, idx: number) => (
                            <li key={idx}>{erro}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                {/* Coluna da Esquerda: Identificação e Dados Gerais (7 colunas) */}
                <div className="lg:col-span-7 space-y-4">
                  <h4 className="text-xs font-bold text-[#7c3aed] dark:text-purple-400 uppercase tracking-wider border-b border-purple-100 dark:border-purple-900/40 pb-1.5 flex items-center gap-2">
                    Identificação & Classificação
                  </h4>

                  {/* Data do Registro */}
                  <div className="bg-slate-50 dark:bg-slate-800/50 p-2.5 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-between text-xs">
                    <span className="font-bold text-slate-600 dark:text-slate-400">Data do Registro:</span>
                    <input 
                      type="date" 
                      className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-2.5 py-1 font-mono text-slate-800 dark:text-slate-200 focus:outline-none focus:border-[#7c3aed]"
                      value={currentExpense.createdAt ? currentExpense.createdAt.split('T')[0] : ''}
                      onChange={(e) => {
                        const newDate = e.target.value;
                        setCurrentExpense(prev => ({ 
                          ...prev, 
                          createdAt: newDate,
                          dueDate: newDate, 
                          paymentDate: prev.status === 'paid' ? newDate : prev.paymentDate 
                        }));
                      }}
                    />
                  </div>

                  {/* Título */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Título</label>
                    <input 
                      type="text" 
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:border-[#7c3aed] focus:outline-none text-sm" 
                      placeholder="Ex: Manutenção do Ar Condicionado"
                      value={currentExpense.title || ''} 
                      onChange={e => setCurrentExpense({...currentExpense, title: e.target.value})} 
                    />
                  </div>

                  {/* Descrição / Fornecedor */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Descrição / Fornecedor</label>
                    <input 
                      type="text" 
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm focus:border-[#7c3aed] focus:outline-none" 
                      placeholder="Ex: Serviço realizado pela empresa Climafrio"
                      value={currentExpense.supplierName || ''} 
                      onChange={e => setCurrentExpense({...currentExpense, supplierName: e.target.value})} 
                    />
                  </div>

                  {/* Tipo & Categoria */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Tipo</label>
                      <select 
                        className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:border-[#7c3aed] focus:outline-none text-sm"
                        value={currentExpense.type || ''} 
                        onChange={handleTypeChange}
                      >
                        {types.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>

                    {currentExpense.type === 'Boleto' && !currentExpense.id ? (
                      <div className="animate-in fade-in zoom-in duration-300">
                        <label className="block text-xs font-semibold text-[#7c3aed] dark:text-purple-400 mb-1 flex items-center gap-1">
                          <Layers size={14} /> Qtd. Parcelas
                        </label>
                        <input 
                          type="number" 
                          min="1" 
                          max="60" 
                          className="w-full border-2 border-purple-100 dark:border-purple-900/40 p-2 rounded-lg bg-white dark:bg-slate-900 text-[#7c3aed] dark:text-purple-400 font-bold focus:border-[#7c3aed] focus:outline-none text-sm"
                          placeholder="Ex: 3"
                          value={installments} 
                          onChange={e => setInstallments(Number(e.target.value))} 
                        />
                      </div>
                    ) : showVehicleSelect ? (
                      <div className="animate-in fade-in zoom-in duration-300">
                        <label className="block text-xs font-semibold text-orange-600 dark:text-orange-400 mb-1 flex items-center gap-1">
                          <Truck size={14} /> Veículo
                        </label>
                        <select 
                          className="w-full border-2 border-orange-100 dark:border-orange-900/40 p-2 rounded-lg bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 focus:border-orange-500 focus:outline-none text-sm"
                          value={currentExpense.vehicle || ''} 
                          onChange={e => setCurrentExpense({...currentExpense, vehicle: e.target.value})}
                        >
                          <option value="">Selecione o veículo...</option>
                          {vehicles.map(v => <option key={v} value={v}>{v}</option>)}
                        </select>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Categoria</label>
                        <select 
                          className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:border-[#7c3aed] focus:outline-none text-sm"
                          value={currentExpense.category || ''} 
                          onChange={e => setCurrentExpense({...currentExpense, category: e.target.value})}
                        >
                          {categories.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    )}
                  </div>

                  {((currentExpense.type === 'Boleto' && !currentExpense.id) || showVehicleSelect) && (
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Categoria</label>
                      <select 
                        className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:border-[#7c3aed] focus:outline-none text-sm"
                        value={currentExpense.category || ''} 
                        onChange={e => setCurrentExpense({...currentExpense, category: e.target.value})}
                      >
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  )}

                  {/* PIX */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">PIX Copia e Cola / Chave PIX</label>
                    <input 
                      type="text" 
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-xs font-mono" 
                      placeholder="Cole a chave PIX ou código Copia e Cola"
                      value={currentExpense.pix || ''} 
                      onChange={e => setCurrentExpense({...currentExpense, pix: e.target.value})} 
                    />
                  </div>

                  {/* Linha Digitável */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Linha Digitável</label>
                    <input 
                      type="text" 
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-xs font-mono" 
                      placeholder="Somente números"
                      value={currentExpense.linha_digitavel || ''} 
                      onChange={e => setCurrentExpense({...currentExpense, linha_digitavel: e.target.value})} 
                    />
                  </div>

                  {currentExpense.codigo_barras && (
                    <div className="flex justify-center bg-white p-3 rounded-lg border border-slate-200 dark:border-slate-700 overflow-x-auto">
                      <Barcode value={currentExpense.codigo_barras} width={1.5} height={45} fontSize={13} background="transparent" />
                    </div>
                  )}
                </div>

                {/* Coluna da Direita: Dados Financeiros & Anexos (5 colunas) */}
                <div className="lg:col-span-5 space-y-4">
                  <h4 className="text-xs font-bold text-[#7c3aed] dark:text-purple-400 uppercase tracking-wider border-b border-purple-100 dark:border-purple-900/40 pb-1.5 flex items-center gap-2">
                    Dados Financeiros & Anexos
                  </h4>

                  {/* Card de Valores Principais */}
                  <div className="bg-purple-50/50 dark:bg-purple-950/20 border border-purple-100 dark:border-purple-900/40 rounded-xl p-4 space-y-3.5">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Valor (R$)</label>
                        <input 
                          type="text"
                          inputMode="numeric" 
                          className="w-full border border-purple-200 dark:border-purple-900/60 p-2.5 rounded-lg font-bold text-base text-purple-950 dark:text-purple-200 bg-white dark:bg-slate-900 focus:border-[#7c3aed] focus:ring-1 focus:ring-[#7c3aed] outline-none" 
                          placeholder="0,00"
                          value={amountInput} 
                          onChange={handleAmountInputChange} 
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Quantidade</label>
                        <input 
                          type="number"
                          min="0"
                          step="any"
                          className="w-full border border-purple-200 dark:border-purple-900/60 p-2.5 rounded-lg font-bold text-base text-purple-950 dark:text-purple-200 bg-white dark:bg-slate-900 focus:border-[#7c3aed] focus:ring-1 focus:ring-[#7c3aed] outline-none" 
                          placeholder="1"
                          value={currentExpense.quantidade !== undefined && currentExpense.quantidade !== null ? currentExpense.quantidade : ""}
                          onChange={e => setCurrentExpense({...currentExpense, quantidade: e.target.value === "" ? undefined : Number(e.target.value)})} 
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Data de Vencimento</label>
                      <input 
                        type="date" 
                        className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm focus:border-[#7c3aed] outline-none" 
                        value={currentExpense.dueDate || ''} 
                        onChange={e => setCurrentExpense({...currentExpense, dueDate: e.target.value})} 
                      />
                    </div>

                    <div className="flex items-center gap-2 p-2.5 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                      <input 
                        type="checkbox" 
                        id="agendaMarkAsPaid"
                        checked={currentExpense.status === 'paid'}
                        onChange={(e) => {
                          const isPaid = e.target.checked;
                          const currentVal = parseFloat(amountInput.replace(/\./g, '').replace(',', '.')) || 0;
                          const newPaymentDate = isPaid ? (currentExpense.dueDate || currentExpense.createdAt || getLocalDateString()) : null;
                          
                          setCurrentExpense(prev => ({
                            ...prev,
                            status: isPaid ? 'paid' : 'open',
                            paidAmount: isPaid ? currentVal : null,
                            paymentDate: newPaymentDate
                          }));
                        }}
                        className="w-4 h-4 text-green-600 dark:text-green-500 rounded focus:ring-green-500 border-gray-300 dark:border-slate-600 dark:bg-slate-800 cursor-pointer"
                      />
                      <label htmlFor="agendaMarkAsPaid" className="text-xs font-bold text-slate-700 dark:text-slate-300 select-none cursor-pointer flex items-center gap-1.5">
                        <CheckCircle size={15} className={currentExpense.status === 'paid' ? 'text-green-600 dark:text-green-500' : 'text-slate-400 dark:text-slate-500'} />
                        Já está pago?
                      </label>
                    </div>

                    {currentExpense.status === 'paid' && (
                      <div className="grid grid-cols-2 gap-3 bg-green-50 dark:bg-green-500/10 p-3 rounded-lg border border-green-200 dark:border-green-500/20 animate-in fade-in duration-200">
                        <div className="col-span-2 text-[10px] font-bold text-green-700 dark:text-green-400 uppercase">Confirmação do Pagamento</div>
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-400 mb-1">Valor Pago</label>
                          <input 
                            type="number" 
                            step="0.01" 
                            className="w-full border border-slate-300 dark:border-slate-700 p-1.5 rounded bg-white dark:bg-slate-900 font-bold text-green-700 dark:text-green-500 text-xs" 
                            value={currentExpense.paidAmount ?? ''} 
                            onChange={e => setCurrentExpense({...currentExpense, paidAmount: parseFloat(e.target.value) || null})} 
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-400 mb-1">Data Pagto</label>
                          <input 
                            type="date" 
                            className="w-full border border-slate-300 dark:border-slate-700 p-1.5 rounded bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-xs" 
                            value={currentExpense.paymentDate || ''} 
                            onChange={e => setCurrentExpense({...currentExpense, paymentDate: e.target.value})} 
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Card de Anexos */}
                  <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-3.5 space-y-2.5">
                    <h5 className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center justify-between">
                      <span className="flex items-center gap-1.5">
                        <Paperclip size={14} className="text-[#7c3aed]" /> Comprovantes e Anexos
                      </span>
                      {currentExpense.attachments && currentExpense.attachments.length > 0 && (
                        <span className="text-[10px] bg-purple-100 dark:bg-purple-900/40 text-[#7c3aed] dark:text-purple-300 px-2 py-0.5 rounded-full font-bold">
                          {currentExpense.attachments.length} {currentExpense.attachments.length === 1 ? 'item' : 'itens'}
                        </span>
                      )}
                    </h5>

                    <div className="space-y-2 max-h-36 overflow-y-auto pr-1">
                      {currentExpense.attachments?.map(att => (
                        <div key={att.id} className="flex items-center justify-between p-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm text-xs">
                          <div className="flex items-center gap-2 overflow-hidden">
                            {att.thumbnailUrl ? (
                              <img src={att.thumbnailUrl} alt={att.name} className="w-8 h-8 object-cover rounded cursor-pointer" onClick={() => handlePreview(att)} referrerPolicy="no-referrer" />
                            ) : (
                              <div className="w-8 h-8 bg-slate-100 dark:bg-slate-800 rounded flex items-center justify-center text-slate-400 cursor-pointer" onClick={() => handlePreview(att)}>
                                <Paperclip size={14} />
                              </div>
                            )}
                            <div className="truncate max-w-[120px] font-medium text-blue-600 dark:text-blue-400 cursor-pointer hover:underline" onClick={() => handlePreview(att)}>{att.name}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button onClick={() => handlePreview(att)} className="text-blue-600 dark:text-blue-400 font-bold hover:underline">Ver</button>
                            <button onClick={() => handleDeleteAttachment(att.id)} className="text-red-400 hover:text-red-600"><X size={14} /></button>
                          </div>
                        </div>
                      ))}

                      {tempAttachment && (
                        <div className="flex items-center justify-between p-2 bg-yellow-50 dark:bg-yellow-500/10 border border-yellow-200 dark:border-yellow-500/20 rounded-lg shadow-sm text-xs animate-pulse">
                          <div className="flex items-center gap-2 overflow-hidden">
                            {tempAttachment.thumbnailUrl ? (
                              <img src={tempAttachment.thumbnailUrl} alt={tempAttachment.name} className="w-8 h-8 object-cover rounded" referrerPolicy="no-referrer" />
                            ) : tempAttachment.name.toLowerCase().endsWith('.pdf') ? (
                              <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900/30 rounded flex items-center justify-center text-yellow-600 dark:text-yellow-500"><FileText size={14} /></div>
                            ) : (
                              <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900/30 rounded flex items-center justify-center text-yellow-600 dark:text-yellow-500"><Paperclip size={14} /></div>
                            )}
                            <span className="truncate max-w-[120px] font-medium text-yellow-800 dark:text-yellow-400">{tempAttachment.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-yellow-600 dark:text-yellow-500 font-bold">Carregado</span>
                            <button onClick={clearTempAttachment} className="text-red-400 hover:text-red-600"><X size={14} /></button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*,application/pdf" className="hidden" />
                      <button 
                        type="button" 
                        onClick={() => fileInputRef.current?.click()} 
                        disabled={isUploading}
                        className="w-full border border-dashed border-purple-300 dark:border-purple-800/80 bg-white dark:bg-slate-900 hover:bg-purple-50/50 dark:hover:bg-purple-950/20 text-[#7c3aed] dark:text-purple-400 py-2.5 rounded-lg text-xs font-bold flex items-center justify-center gap-2 transition-colors"
                      >
                        <Camera size={16} /> {isUploading ? 'Enviando...' : 'Adicionar Foto ou Comprovante'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 dark:bg-slate-800/70 border-t border-slate-200 dark:border-slate-700/80 px-6 py-3.5 flex justify-end gap-3 shrink-0 rounded-b-2xl">
              <button 
                type="button" 
                onClick={() => {
                  setIsModalOpen(false);
                  setDocumentMetadata(null);
                }} 
                className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800 border border-transparent hover:border-slate-300 dark:hover:border-slate-600 rounded-lg transition-colors font-medium"
              >
                Cancelar
              </button>
              <button 
                type="button" 
                onClick={handleSave} 
                disabled={isSaving || isUploading} 
                className="px-6 py-2 text-sm text-white bg-green-600 hover:bg-green-700 font-bold uppercase rounded-lg shadow-md flex items-center gap-2 disabled:opacity-50 transition-all hover:scale-[1.02] active:scale-[0.98]"
              >
                {isSaving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Reversão de Pagamento */}
      {isRevertModalOpen && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 z-[70] flex items-center justify-center p-2 sm:p-4 backdrop-blur-sm">
           <div className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 shadow-2xl w-full max-w-sm rounded-lg flex flex-col overflow-hidden animate-in zoom-in duration-200">
              <div className="bg-orange-600 dark:bg-orange-700 text-white px-4 sm:px-6 py-3 flex justify-between items-center shrink-0">
                 <h3 className="font-bold text-sm uppercase flex items-center gap-2">
                    <RotateCcw size={18}/> Reverter Pagamento
                 </h3>
                 <button onClick={() => setIsRevertModalOpen(false)} className="hover:text-orange-200"><X size={20}/></button>
              </div>
              <div className="p-4 sm:p-6 space-y-6">
                  <div className="text-center font-sans">
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-2 font-medium">Lançamento: <span className="text-slate-800 dark:text-slate-200 italic">"{revertingExpense?.title}"</span></p>
                      <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">Como você deseja reabrir este lançamento?</p>
                      <div className="grid grid-cols-1 gap-3">
                         <button 
                            onClick={() => handleConfirmRevert('open')}
                            className="w-full flex items-center justify-center gap-2 py-3 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 font-bold transition-all text-xs uppercase"
                         >
                            <Calendar size={18} /> Colocar como Aberto (Pendente)
                         </button>
                         <button 
                            onClick={() => handleConfirmRevert('overdue')}
                            className="w-full flex items-center justify-center gap-2 py-3 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 font-bold transition-all text-xs uppercase"
                         >
                            <AlertCircle size={18} /> Colocar como Vencido
                         </button>
                      </div>
                  </div>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800/50 px-4 sm:px-6 py-4 flex justify-center border-t border-slate-100 dark:border-slate-800">
                  <button onClick={() => setIsRevertModalOpen(false)} className="px-4 py-2 text-sm text-slate-500 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800 rounded transition-colors font-medium">Manter como Pago</button>
              </div>
           </div>
        </div>
      )}

      {/* Modal de Pagamento (Data e Valor Pago) */}
      {isPaymentModalOpen && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 z-[60] flex items-center justify-center p-2 sm:p-4 backdrop-blur-sm">
           <div className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 shadow-2xl w-full max-w-sm rounded-lg flex flex-col overflow-hidden animate-in zoom-in duration-200">
              <div className="bg-green-700 dark:bg-green-800 text-white px-4 sm:px-6 py-3 flex justify-between items-center shrink-0">
                 <h3 className="font-bold text-sm uppercase flex items-center gap-2">
                    <CheckCircle size={18}/> Confirmar Pagamento
                 </h3>
                 <button onClick={() => setIsPaymentModalOpen(false)} className="hover:text-green-200"><X size={20}/></button>
              </div>
              <div className="p-4 sm:p-6 space-y-4 font-sans">
                  <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-2 italic">"{payingExpense?.title}"</p>
                      <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1 uppercase tracking-wider">Valor Pago (R$)</label>
                      <input 
                        type="number" 
                        step="0.01" 
                        className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 rounded font-bold text-lg text-green-700 dark:text-green-500 focus:outline-none focus:border-green-500"
                        value={paidValue}
                        onChange={(e) => setPaidValue(Number(e.target.value))}
                      />
                      <p className="text-[10px] text-slate-400 mt-1">Valor original: {formatCurrency(payingExpense?.amount || 0)}</p>
                  </div>
                  <div>
                      <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1 uppercase tracking-wider">Data do Pagamento</label>
                      <input 
                        type="date" 
                        className="w-full border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 rounded text-slate-800 dark:text-slate-200 focus:outline-none focus:border-green-500"
                        value={paidDate}
                        onChange={(e) => setPaidDate(e.target.value)}
                      />
                  </div>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800/50 px-4 sm:px-6 py-4 flex justify-end gap-3 border-t border-slate-100 dark:border-slate-800">
                  <button onClick={() => setIsPaymentModalOpen(false)} className="px-4 py-2 text-sm text-slate-500 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800 rounded transition-colors font-medium">Cancelar</button>
                  <button onClick={handleConfirmPayment} className="px-6 py-2 bg-green-600 text-white font-bold uppercase text-xs rounded shadow-lg shadow-green-200 dark:shadow-none hover:bg-green-700 transition-all flex items-center gap-2">
                     Confirmar
                  </button>
              </div>
           </div>
        </div>
      )}

      {/* Modal de Exclusão (Substitui confirm do navegador) */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 z-[90] flex items-center justify-center p-2 sm:p-4 backdrop-blur-sm">
           <div className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 shadow-2xl w-full max-w-sm rounded-lg flex flex-col overflow-hidden animate-in zoom-in duration-200">
              <div className="bg-red-600 dark:bg-red-700 text-white px-4 sm:px-6 py-3 flex justify-between items-center shrink-0">
                 <h3 className="font-bold text-sm uppercase flex items-center gap-2">
                    <Trash2 size={18}/> Excluir Lançamento
                 </h3>
                 <button onClick={() => setIsDeleteModalOpen(false)} className="hover:text-red-200"><X size={20}/></button>
              </div>
              <div className="p-4 sm:p-6 text-center">
                  <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4 text-red-600 dark:text-red-400">
                      <AlertTriangle size={24} />
                  </div>
                  <h4 className="font-bold text-slate-800 dark:text-slate-200 mb-2">Tem certeza?</h4>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">Esta ação não poderá ser desfeita. O lançamento será removido permanentemente.</p>
                  <div className="flex justify-center gap-3">
                      <button onClick={() => setIsDeleteModalOpen(false)} className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded transition-colors font-medium">Cancelar</button>
                      <button onClick={handleConfirmDelete} className="px-6 py-2 text-sm text-white bg-red-600 hover:bg-red-700 font-bold uppercase rounded shadow-sm">
                          Sim, Excluir
                      </button>
                  </div>
              </div>
           </div>
        </div>
      )}

      {/* Modal de Visualização (Read Only) */}
      {isViewModalOpen && viewExpense && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 z-[80] flex items-center justify-center p-3 sm:p-4 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl w-full max-w-5xl rounded-2xl flex flex-col max-h-[92vh] sm:max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-[#4c1d95] via-[#5b21b6] to-[#3b0764] text-white px-6 py-3.5 flex justify-between items-center shrink-0 shadow-md">
              <h3 className="font-bold text-base flex items-center gap-2 tracking-wide">
                <Eye size={18}/> Visualizar Lançamento
              </h3>
              <button onClick={() => setIsViewModalOpen(false)} className="text-white/80 hover:text-white hover:bg-white/10 p-1.5 rounded-full transition-colors">
                <X size={20}/>
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                {/* Coluna da Esquerda (7 colunas) */}
                <div className="lg:col-span-7 space-y-4">
                  <h4 className="text-xs font-bold text-[#7c3aed] dark:text-purple-400 uppercase tracking-wider border-b border-purple-100 dark:border-purple-900/40 pb-1.5 flex items-center gap-2">
                    Identificação & Classificação
                  </h4>

                  <div className="bg-slate-50 dark:bg-slate-800/50 p-2.5 rounded-lg border border-slate-200 dark:border-slate-800 flex items-center justify-between text-xs">
                    <span className="font-bold text-slate-600 dark:text-slate-400">Data do Registro:</span>
                    <input 
                      type="date" 
                      disabled 
                      className="bg-transparent border-none font-mono text-slate-800 dark:text-slate-200"
                      value={viewExpense.createdAt ? viewExpense.createdAt.split('T')[0] : ''}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Título</label>
                    <input type="text" disabled className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm font-medium" 
                           value={viewExpense.title || ''} />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Descrição / Fornecedor</label>
                    <input type="text" disabled className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm" 
                           value={viewExpense.supplierName || ''} />
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Tipo</label>
                      <input type="text" disabled className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm" 
                             value={viewExpense.type || ''} />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Categoria</label>
                      <input type="text" disabled className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm" 
                             value={viewExpense.category || ''} />
                    </div>
                  </div>

                  {viewExpense.vehicle && (
                    <div>
                      <label className="block text-xs font-semibold text-orange-600 dark:text-orange-400 mb-1">Veículo</label>
                      <input type="text" disabled className="w-full border border-orange-200 dark:border-orange-900/40 p-2 rounded-lg bg-orange-50/50 dark:bg-orange-900/20 text-orange-900 dark:text-orange-200 text-sm" 
                             value={viewExpense.vehicle} />
                    </div>
                  )}

                  {viewExpense.pix && (
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">PIX Copia e Cola / Chave PIX</label>
                      <input type="text" disabled className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-xs font-mono" 
                             value={viewExpense.pix} />
                    </div>
                  )}

                  {viewExpense.linha_digitavel && (
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Linha Digitável</label>
                      <input type="text" disabled className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-xs font-mono" 
                             value={viewExpense.linha_digitavel} />
                    </div>
                  )}

                  {viewExpense.codigo_barras && (
                    <div className="flex justify-center bg-white p-3 rounded-lg border border-slate-200 dark:border-slate-700 overflow-x-auto">
                      <Barcode value={viewExpense.codigo_barras} width={1.5} height={45} fontSize={13} background="transparent" />
                    </div>
                  )}
                </div>

                {/* Coluna da Direita (5 colunas) */}
                <div className="lg:col-span-5 space-y-4">
                  <h4 className="text-xs font-bold text-[#7c3aed] dark:text-purple-400 uppercase tracking-wider border-b border-purple-100 dark:border-purple-900/40 pb-1.5 flex items-center gap-2">
                    Dados Financeiros & Anexos
                  </h4>

                  <div className="bg-purple-50/50 dark:bg-purple-950/20 border border-purple-100 dark:border-purple-900/40 rounded-xl p-4 space-y-3.5">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Valor (R$)</label>
                        <input type="text" disabled className="w-full border border-purple-200 dark:border-purple-900/60 p-2.5 rounded-lg font-bold text-base text-purple-950 dark:text-purple-200 bg-white dark:bg-slate-900" 
                               value={formatCurrency(viewExpense.amount)} />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Quantidade</label>
                        <input type="text" disabled className="w-full border border-purple-200 dark:border-purple-900/60 p-2.5 rounded-lg font-bold text-base text-purple-950 dark:text-purple-200 bg-white dark:bg-slate-900" 
                               value={viewExpense.quantidade ?? 1} />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Data de Vencimento</label>
                      <input type="date" disabled className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-sm" 
                             value={viewExpense.dueDate || ''} />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">Status</label>
                      <div className="p-2.5 rounded-lg border font-bold text-xs uppercase flex items-center gap-2 bg-white dark:bg-slate-900">
                        {viewExpense.status === 'paid' ? (
                          <span className="text-green-600 dark:text-green-400 flex items-center gap-1.5"><CheckCircle size={15}/> Pago</span>
                        ) : viewExpense.status === 'overdue' ? (
                          <span className="text-red-600 dark:text-red-400 flex items-center gap-1.5"><AlertCircle size={15}/> Vencido</span>
                        ) : (
                          <span className="text-blue-600 dark:text-blue-400 flex items-center gap-1.5"><Calendar size={15}/> Em Aberto</span>
                        )}
                      </div>
                    </div>

                    {viewExpense.status === 'paid' && (
                      <div className="grid grid-cols-2 gap-3 bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-200 dark:border-green-800/50">
                        <div className="col-span-2 text-[10px] font-bold text-green-700 dark:text-green-400 uppercase">Dados de Pagamento</div>
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-400 mb-1">Valor Pago</label>
                          <input type="text" disabled className="w-full border border-slate-300 dark:border-slate-700 p-1.5 rounded bg-white dark:bg-slate-800 font-bold text-green-700 dark:text-green-400 text-xs" 
                                 value={formatCurrency(viewExpense.paidAmount || 0)} />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-400 mb-1">Data Pagto</label>
                          <input type="date" disabled className="w-full border border-slate-300 dark:border-slate-700 p-1.5 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-xs" 
                                 value={viewExpense.paymentDate || ''} />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-3.5 space-y-2.5">
                    <h5 className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center justify-between">
                      <span className="flex items-center gap-1.5">
                        <Paperclip size={14} className="text-[#7c3aed]" /> Comprovantes e Anexos
                      </span>
                      {viewExpense.attachments && viewExpense.attachments.length > 0 && (
                        <span className="text-[10px] bg-purple-100 dark:bg-purple-900/40 text-[#7c3aed] dark:text-purple-300 px-2 py-0.5 rounded-full font-bold">
                          {viewExpense.attachments.length} {viewExpense.attachments.length === 1 ? 'item' : 'itens'}
                        </span>
                      )}
                    </h5>
                    <div className="space-y-2 max-h-36 overflow-y-auto pr-1">
                      {(!viewExpense.attachments || viewExpense.attachments.length === 0) && <p className="text-xs text-slate-400 italic">Sem anexos.</p>}
                      {viewExpense.attachments?.map(att => (
                        <div key={att.id} className="flex items-center justify-between p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm text-xs">
                          <div className="flex items-center gap-2 overflow-hidden">
                            {att.thumbnailUrl ? (
                              <img src={att.thumbnailUrl} alt={att.name} className="w-8 h-8 object-cover rounded cursor-pointer" onClick={() => handlePreview(att)} referrerPolicy="no-referrer" />
                            ) : (
                              <div className="w-8 h-8 bg-slate-100 dark:bg-slate-700 rounded flex items-center justify-center text-slate-400 cursor-pointer" onClick={() => handlePreview(att)}>
                                <Paperclip size={14} />
                              </div>
                            )}
                            <div className="truncate max-w-[120px] font-medium text-blue-600 dark:text-blue-400 cursor-pointer hover:underline" onClick={() => handlePreview(att)}>{att.name}</div>
                          </div>
                          <button onClick={() => handlePreview(att)} className="text-blue-600 dark:text-blue-400 font-bold hover:underline">Ver</button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 dark:bg-slate-800/70 border-t border-slate-200 dark:border-slate-700/80 px-6 py-3.5 flex justify-end gap-3 shrink-0 rounded-b-2xl">
              <button type="button" onClick={() => setIsViewModalOpen(false)} className="px-5 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800 border border-transparent hover:border-slate-300 dark:hover:border-slate-600 rounded-lg transition-colors font-medium">
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Preview do Anexo */}
      {previewUrl && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-2 sm:p-4 animate-in fade-in duration-200">
            <div className="bg-white dark:bg-slate-900 rounded-lg p-2 max-w-4xl w-full max-h-[95vh] flex flex-col relative">
                <div className="flex justify-between items-center p-2 border-b dark:border-slate-800 mb-2">
                    <h3 className="font-bold text-slate-700 dark:text-slate-200">Visualização do Anexo</h3>
                    <button onClick={() => setPreviewUrl(null)} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full">
                        <X size={24} className="text-slate-500 dark:text-slate-400" />
                    </button>
                </div>
                <div className="flex-1 overflow-auto flex justify-center items-center bg-slate-100 dark:bg-slate-950 rounded min-h-[300px]">
                    {previewUrl && (previewUrl.toLowerCase().endsWith('.pdf') || previewUrl.includes('drive.google.com/file/d/')) ? (
                       <iframe src={previewUrl.includes('/view') ? previewUrl.replace(/\/view.*/, '/preview') : previewUrl} className="w-full h-full min-h-[500px]" title="Document Preview"></iframe>
                    ) : (
                       <img src={previewUrl} alt="Preview" className="max-w-full max-h-[70vh] object-contain" referrerPolicy="no-referrer" />
                    )}
                </div>
                <div className="p-4 flex justify-center">
                    <a 
                        href={previewUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-bold rounded-full hover:bg-blue-700 transition-colors shadow-lg"
                    >
                        <ExternalLink size={18} /> Visualizar em nova guia
                    </a>
                </div>
            </div>
        </div>
      )}
      {isRevenueModalOpen && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 z-50 flex items-center justify-center p-2 sm:p-4 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 shadow-xl w-full max-w-3xl rounded-lg flex flex-col max-h-[95vh] sm:max-h-[85vh]">
            <div className="bg-gradient-to-r from-brand-800 to-brand-900 text-white px-4 sm:px-6 py-3 flex justify-between items-center shrink-0 rounded-t-lg">
              <h3 className="font-bold text-base flex items-center gap-2">
                {isEditing ? (
                  isViewOnly ? (
                    <Eye size={18} />
                  ) : (
                    <Edit size={18} />
                  )
                ) : (
                  <Plus size={18} />
                )}
                {isEditing
                  ? isViewOnly
                    ? "Visualizar Receita"
                    : "Editar Receita"
                  : "Nova Receita"}
              </h3>
              <button
                onClick={() => setIsRevenueModalOpen(false)}
                className="hover:text-brand-200"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 sm:p-6">
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div className="sm:col-span-2">
                    <label className="block font-semibold text-slate-600 dark:text-slate-400 mb-1">
                      Cliente
                    </label>
                    <input
                      type="text"
                      disabled={isViewOnly}
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded focus:border-brand-500 focus:outline-none disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
                      placeholder="Nome do Cliente"
                      value={currentRevenue.cliente || ""}
                      onChange={(e) =>
                        setCurrentRevenue({
                          ...currentRevenue,
                          cliente: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block font-semibold text-slate-600 dark:text-slate-400 mb-1">
                      Descrição
                    </label>
                    <input
                      type="text"
                      disabled={isViewOnly}
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
                      placeholder="Descrição do serviço/produto"
                      value={currentRevenue.descricao || ""}
                      onChange={(e) =>
                        setCurrentRevenue({
                          ...currentRevenue,
                          descricao: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div>
                    <label className="block font-semibold text-slate-600 dark:text-slate-400 mb-1">
                      Número da Nota
                    </label>
                    <input
                      type="text"
                      disabled={isViewOnly}
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
                      value={currentRevenue.numero_nota || ""}
                      onChange={(e) =>
                        setCurrentRevenue({
                          ...currentRevenue,
                          numero_nota: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-slate-600 dark:text-slate-400 mb-1">
                      Série da Nota
                    </label>
                    <input
                      type="text"
                      disabled={isViewOnly}
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
                      value={currentRevenue.serie_nota || ""}
                      onChange={(e) =>
                        setCurrentRevenue({
                          ...currentRevenue,
                          serie_nota: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div>
                    <label className="block font-semibold text-slate-600 dark:text-slate-400 mb-1">
                      Tipo de Nota
                    </label>
                    <select
                      disabled={isViewOnly}
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded bg-white dark:bg-slate-900 disabled:bg-slate-50 dark:disabled:bg-slate-800/50 text-slate-800 dark:text-slate-200"
                      value={currentRevenue.tipo_nota || ''}
                      onChange={(e) =>
                        setCurrentRevenue({
                          ...currentRevenue,
                          tipo_nota: e.target.value,
                        })
                      }
                    >
                      <option value="NFE">NFE</option>
                      <option value="NFSE">NFSE</option>
                      <option value="OUTRO">OUTRO</option>
                    </select>
                  </div>
                  <div>
                    <label className="block font-semibold text-slate-600 dark:text-slate-400 mb-1">
                      Status
                    </label>
                    <select
                      disabled={isViewOnly}
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded bg-white dark:bg-slate-900 disabled:bg-slate-50 dark:disabled:bg-slate-800/50 text-slate-800 dark:text-slate-200"
                      value={currentRevenue.status || ''}
                      onChange={(e) =>
                        setCurrentRevenue({
                          ...currentRevenue,
                          status: e.target.value as any,
                        })
                      }
                    >
                      <option value="EM_ABERTO">Em Aberto</option>
                      <option value="RECEBIDO">Recebido</option>
                      <option value="VENCIDO">Vencido</option>
                      <option value="CANCELADO">Cancelado</option>
                    </select>
                  </div>

                  <div>
                    <label className="block font-semibold text-slate-600 dark:text-slate-400 mb-1">
                      Data de Emissão
                    </label>
                    <input
                      type="date"
                      disabled={isViewOnly}
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
                      value={currentRevenue.data_emissao || ""}
                      onChange={(e) =>
                        setCurrentRevenue({
                          ...currentRevenue,
                          data_emissao: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-slate-600 dark:text-slate-400 mb-1">
                      Previsão de Pagamento
                    </label>
                    <input
                      type="date"
                      disabled={isViewOnly}
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
                      value={currentRevenue.previsao_pagamento || ""}
                      onChange={(e) =>
                        setCurrentRevenue({
                          ...currentRevenue,
                          previsao_pagamento: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div>
                    <label className="block font-semibold text-slate-600 dark:text-slate-400 mb-1">
                      Valor Bruto (R$)
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      disabled={isViewOnly}
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded font-bold text-slate-700 dark:text-slate-200 disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-900 focus:border-brand-500 outline-none"
                      value={amountInput}
                      onChange={handleRevenueAmountInputChange}
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-slate-600 dark:text-slate-400 mb-1">
                      Desconto (R$)
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      disabled={isViewOnly}
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded font-bold text-slate-700 dark:text-slate-200 disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-900 focus:border-brand-500 outline-none"
                      value={discountInput}
                      onChange={handleDiscountInputChange}
                    />
                  </div>

                  <div>
                    <label className="block font-semibold text-slate-600 dark:text-slate-400 mb-1">
                      Impostos (R$)
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      disabled={isViewOnly}
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded font-bold text-slate-700 dark:text-slate-200 disabled:bg-slate-50 dark:disabled:bg-slate-800/50 bg-white dark:bg-slate-900 focus:border-brand-500 outline-none"
                      value={taxInput}
                      onChange={handleTaxInputChange}
                    />
                  </div>

                  <div>
                    <label className="block font-semibold text-slate-600 dark:text-slate-400 mb-1">
                      Valor Líquido (R$)
                    </label>
                    <input
                      type="text"
                      disabled
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded font-bold text-green-700 dark:text-green-500 bg-slate-100 dark:bg-slate-800/50 outline-none"
                      value={formatCurrency(currentRevenue.valor_liquido)}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 px-4 sm:px-6 py-4 flex justify-end gap-3 shrink-0 rounded-b-lg">
              <button
                type="button"
                onClick={() => setIsRevenueModalOpen(false)}
                className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800 border border-transparent hover:border-slate-300 dark:hover:border-slate-600 rounded transition-colors"
              >
                {isViewOnly ? "Fechar" : "Cancelar"}
              </button>
              {!isViewOnly && (
                <button
                  type="button"
                  onClick={handleSaveRevenue}
                  disabled={isSaving}
                  className="px-6 py-2 text-sm text-white bg-green-600 hover:bg-green-700 font-bold uppercase rounded shadow-sm flex items-center gap-2 disabled:opacity-50"
                >
                  {isSaving ? "..." : "Salvar"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Agenda;